import {filter, startWith} from 'rxjs';
import {useEffect} from 'react';

import {
  Conversation,
  ConversationEvent,
} from '@stripe-internal/conversations-client';
import {playSound, SoundSetting} from '@stripe-internal/conversations-ui';

import {useDocumentVisibility} from './useDocumentVisibility';
import {useVisibility} from '../../../core/hooks/appHooks';

function isAgentMessageEvent(
  event: ConversationEvent,
  conversation: Conversation,
) {
  return (
    event.name === 'conversation-message-added' &&
    Object.values(conversation.participants).some(
      (participant) =>
        participant.id === event.participantId && participant.role === 'agent',
    )
  );
}

function shouldPlaySound(conversation: Conversation) {
  return (
    conversation.isTabMostRecentlyInteracted() &&
    conversation.context.channel === 'chat' &&
    conversation.context.soundSetting === 'on'
  );
}

function setContextValueIfChanged(
  conversation: Conversation,
  key: string,
  value: unknown,
) {
  if (conversation.context[key] !== value) {
    conversation.setContextValue(key, value);
  }
}

export function useConversationMessageSounds(conversation: Conversation) {
  const visibility = useVisibility();
  const documentVisibility = useDocumentVisibility();

  useEffect(() => {
    const sub = conversation
      .contextObservable()
      .pipe(startWith(conversation.context))
      .subscribe(() => {
        const {context} = conversation;

        if (context.channel !== 'chat' || !conversation.isActive) {
          return;
        }

        const currentSoundSetting = (context.soundSetting ??
          'disabled') as SoundSetting;

        if (['on', 'off'].includes(currentSoundSetting)) {
          return;
        }

        setContextValueIfChanged(conversation, 'soundSetting', 'on');
      });

    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    const sub = conversation
      .statusObservable()
      .pipe(startWith(conversation.status))
      .subscribe(() => {
        if (!conversation.isActive) {
          setContextValueIfChanged(conversation, 'soundSetting', 'disabled');
        }
      });
    return () => sub.unsubscribe();
  }, [conversation]);

  useEffect(() => {
    const soundNotifiableEvents$ = conversation
      .eventsObservable()
      .pipe(filter((event) => isAgentMessageEvent(event, conversation)));

    const sub = soundNotifiableEvents$.subscribe(async (_) => {
      if (shouldPlaySound(conversation)) {
        const soundPlayed = await playSound('new-message');
        conversation.client.analytics.action(
          'sound_notification_triggered_on_new_message',
          {
            soundPlayed,
          },
        );
      }
    });
    return () => sub.unsubscribe();
  }, [conversation, documentVisibility, visibility]);
}
